import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { getCallbackApi } from '@/api/authApi'
import { useUserStore } from '@/store/userStore'
import { getUserInfoApi } from '@/api/userApi'
import { useAuthStore } from '@/store/authStore'
import { logger } from '@/utils/logger'
import { backupTokensToNative } from '@/utils/nativeAuth'
import { handlePostLoginFcmSetup } from '@/utils/fcmTokenManager'
import { isWebView } from '@/utils/webView'
import { useInvalidateAuth } from '@/hooks/queries/auth'
import qbeeHappyTransparent from '@/assets/images/qbee-character/qbee-happy-transparent.png'

export default function SignupCheckPage() {
  const { provider } = useParams()
  const { setUserInfo } = useUserStore()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const callbackUser = searchParams.get('user')
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const invalidateAuth = useInvalidateAuth()

  useEffect(() => {
    const handleSignupCheck = async () => {
      try {
        // 필수 파라미터 검증
        if (!provider) {
          console.error('Provider 파라미터가 없습니다.')
          navigate('/auth/login')
          return
        }

        if (!code) {
          console.error('인증 코드가 없습니다.')
          navigate('/auth/login')
          return
        }

        console.log(`[SignupCheck] ${provider} 로그인 처리 시작`, {
          code: code?.substring(0, 10) + '...',
          callbackUser
        })

        const response = await getCallbackApi(provider, code, callbackUser ?? '')

        if (!response.data) {
          throw new Error('서버 응답 데이터가 없습니다.')
        }

        const { access_token, refresh_token, user } = response.data

        if (!access_token || !user) {
          throw new Error('로그인 응답 데이터가 올바르지 않습니다.')
        }

        // 1. 메모리에 저장
        useAuthStore.getState().setAccessToken(access_token)
        useAuthStore.getState().setRefreshToken(refresh_token)
        useAuthStore.getState().setUserInfo(user)
        logger.success('토큰 메모리 저장 완료')

        // 2. 네이티브에 백업
        backupTokensToNative(access_token, refresh_token, user)

        // 3. FCM 토큰 등록 (WebView 환경, 비동기)
        if (isWebView()) {
          logger.fcm('FCM 토큰 등록 시작')
          setTimeout(() => {
            handlePostLoginFcmSetup().catch(error => {
              logger.warn('FCM 토큰 등록 실패:', error)
            })
          }, 1000)
        }

        logger.success(`${provider} 로그인 완료`)

        // 4. 인증 상태 캐시 무효화
        invalidateAuth()

        // 5. 사용자 정보 조회
        const userInfoResponse = await getUserInfoApi(user.id)
        setUserInfo(userInfoResponse.data)

        // 리다이렉트
        if (user.is_agree) {
          logger.auth('회원가입 완료 → 홈으로 이동')
          navigate(`/`)
        } else {
          logger.auth('회원가입 미완료 → TOS 페이지로 이동')
          navigate(`/auth/tos?provider=${provider}&user_id=${user.id}`)
        }
      } catch (error: any) {
        console.error(`[SignupCheck] ${provider} 로그인 처리 실패:`, error)

        // 에러 메시지 설정
        let errorMessage = '로그인 처리 중 오류가 발생했습니다.'

        if (error.response) {
          // 백엔드에서 반환한 에러
          const status = error.response.status
          const data = error.response.data

          if (status === 400) {
            errorMessage = data.message || '잘못된 인증 정보입니다.'
          } else if (status === 401) {
            errorMessage = '인증에 실패했습니다.'
          } else if (status === 404) {
            errorMessage = '지원하지 않는 로그인 방식입니다.'
          } else {
            errorMessage = '서버 오류가 발생했습니다.'
          }
        } else if (error.request) {
          // 네트워크 오류
          errorMessage = '네트워크 연결을 확인해주세요.'
        }

        setError(errorMessage)

        // 3초 후 로그인 페이지로 이동
        setTimeout(() => {
          navigate('/auth/login')
        }, 3000)
      }
    }

    handleSignupCheck()
  }, [provider, code, navigate, setUserInfo, callbackUser])

  return (
    <div className='min-h-screen bg-gradient-to-r from-amber-400 to-yellow-300 flex items-center justify-center relative overflow-hidden'>
      {/* 배경 장식 요소들 */}
      <div className='absolute top-20 left-10 w-16 h-16 bg-white/20 rounded-full animate-pulse'></div>
      <div className='absolute top-40 right-16 w-12 h-12 bg-white/15 rounded-full animate-pulse delay-300'></div>
      <div className='absolute bottom-32 left-20 w-20 h-20 bg-white/10 rounded-full animate-pulse delay-500'></div>
      <div className='absolute bottom-20 right-10 w-14 h-14 bg-white/20 rounded-full animate-pulse delay-700'></div>

      {/* 메인 콘텐츠 */}
      <div className='text-center z-10 px-8'>
        <div className='bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/30'>
          <div className='flex justify-center mb-6'>
            <div className='relative'>
              {error ? (
                <div className='text-4xl animate-none'>😵</div>
              ) : (
                <img src={qbeeHappyTransparent} alt='큐비 캐릭터' className='w-16 h-16 animate-bounce' />
              )}
              {!error && <div className='absolute -top-2 -right-2 text-2xl animate-pulse delay-300'>✨</div>}
            </div>
          </div>

          {error ? (
            <>
              <h1 className='text-red-700 text-xl font-bold leading-relaxed mb-2'>로그인 실패!</h1>
              <p className='text-red-600 text-base mb-4'>{error}</p>
              <p className='text-amber-800 text-sm'>잠시 후 로그인 페이지로 이동할게!</p>
            </>
          ) : (
            <>
              <h1 className='text-amber-900 text-xl font-bold leading-relaxed mb-2'>로그인 처리 중이야!</h1>
              <p className='text-amber-800 text-base'>잠시만 기다려줘!</p>

              {/* 로딩 인디케이터 */}
              <div className='mt-6 flex justify-center'>
                <div className='w-8 h-8 border-3 border-amber-200 border-t-amber-600 rounded-full animate-spin'></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
